// The landing page (client/index.html #overlay, client/src/landing.js) and
// the landing strings main.js writes (device-card reasons, the 📍 / 🎤
// labels, head count, roster, world line). Tone: punchy + playful, informal
// "du", Swiss spelling (ss, never ß). check-landing.mjs measures the longer
// of EN / DE for every label it fits; check-i18n.mjs holds both languages.
import { register } from '../i18n.js';

register({
  en: {
    'land.lang': 'Language',
    'land.overline': 'Real terrain · Real multiplayer · <b>Plays in VR goggles</b>',
    'land.tagline': 'Real mountains, 1:1. Pick a screen, jump in.',
    'land.live': 'Now live',
    'land.loading': 'loading…',
    'land.name': 'Your name',
    'land.rec': 'Recommended',
    'land.vr.pitch': 'Full immersion — stand on the mountain',
    'land.vr.need': 'Quest / WebXR headset',
    'land.flat.name': 'Desktop',
    'land.flat.pitch': 'Fastest start — click and go',
    'land.flat.need': 'keyboard + mouse',
    'land.mob.name': 'Phone',
    'land.mob.pitch': 'Walk it for real, with GPS',
    'land.mob.need': 'touch + tilt, landscape',
    // devOff (main.js): a mode this device cannot run, in place of the need
    'land.off.vrXr': 'needs a WebXR headset (Quest browser)',
    'land.off.vrHttps': 'needs HTTPS + a WebXR headset',
    'land.off.mob': 'open this page on your phone',
    // gpsBtnSync (main.js)
    'land.gps.use': '📍 Use GPS',
    'land.gps.asking': '📍 GPS: asking…',
    'land.gps.fix': '📍 GPS ±{m} m',
    'land.gps.nofix': '📍 GPS: no fix yet — tap for help',
    'land.gps.off': '📍 GPS unavailable — tap for help',
    'land.gps.retry': 'Try again',
    'land.gps.dismiss': 'Dismiss',
    'land.voice': '🎤 Enable voice chat',
    'land.voice.ready': '🎤 Voice ready — open mic ([M] / left trigger mutes)',
    'land.voice.fail': '🎤 Mic unavailable: {err}',
    'land.loaderAria': 'Loading world',
    'land.status.connecting': 'connecting…',
    // head count + roster (main.js renderRoster / _popText)
    'land.pop.some': '👥 {in} in the world · 👁 {map} on the map{room}',
    'land.pop.none': '👥 nobody in yet — be the first{room}',
    'land.pop.room': ' · room for {n}',
    'land.roster.you': 'you',
    'land.roster.in': 'in the world',
    'land.roster.map': 'on the map',
    'land.roster.bot': '🤖 {n} bot roaming the terrain',
    'land.roster.bots': '🤖 {n} bots roaming the terrain',
    // the world line (main.js nameWorld): the label and provider are data
    'land.world.this': 'this world',
    'land.world.real': 'real terrain',
    // the video frame (landing.js LANDING_VIDEO)
    'land.video.soon': 'Video coming soon',
    'land.video.play': 'Play the video',
    'land.video.yt': 'Loads from YouTube (no cookies) only when you click',
    'land.lede': 'A real place, rebuilt at <b>1:1 scale</b> from surveyed open geodata — mountains, lakes, forests, villages and railways exactly where they truly stand. <strong>Nothing here is invented terrain.</strong>',
    'land.tiles': 'What’s out there',
    'land.more': 'tap for more',
    'land.f.together.h': 'Meet the others there',
    'land.f.together.l': 'Same world, live — hear them where they stand',
    'land.f.together.d': 'Everyone online walks the same world, their name on their avatar and on the Map. Turn on 🎤 and you hear each other where you stand — close and clear beside you, faint across the slope.',
    'land.f.ride.h': 'Ride what the locals ride',
    'land.f.ride.l': 'Funiculars, gondolas, chairlifts — real lines',
    'land.f.ride.d': 'Funiculars, gondolas, cable cars and chairlifts run on their real alignments — board at the station, ride up, step off at a station or anywhere along the line.',
    'land.f.under.h': 'Go underground',
    'land.f.under.l': 'Bored tunnels, walkable viaducts',
    'land.f.under.d': 'Rail tunnels are bored through the mountains and viaducts span the gorges, walkable end to end — as surveyed, not painted on.',
    'land.f.far.h': 'A horizon that’s real',
    'land.f.far.l': 'Named peaks up to 100 km out',
    'land.f.far.d': 'Named peaks stand on the skyline up to 100 km out, dropped by true earth curvature. Zoom in and they are labelled, and the rangefinder reads the distance to whatever you sight.',
    'land.f.any.h': 'Any screen',
    'land.f.any.l': 'VR, desktop or phone — ⤳ Beam in',
    'land.f.any.d': 'VR goggles, desktop or phone — open the link, pick a spot on the Map, ⤳ Beam in. On a phone a stick walks and the gyroscope looks around; GPS can put you on your real position.',
    // the phone how-to (index.html .howto, the looping drawing)
    'land.ph.h': 'Your phone is a window',
    'land.ph.s1': 'Turn it sideways',
    'land.ph.s2': 'Hold it up and turn around — with GPS it shows what’s behind it',
    'land.ph.s3': 'Lay it flat: the Map',
    'land.rotate': '<b>Worlds rotate.</b> One world is live at a time and it can change at any moment — today’s mountain may be a different one tomorrow.',
    'land.swiss': 'The project focuses on Switzerland, standing on the excellent open geodata of <a href="https://www.swisstopo.admin.ch" target="_blank" rel="noopener">swisstopo</a> — LiDAR terrain to half a metre, footprint-true buildings, aerial imagery. Worlds elsewhere run on global open data at coarser fidelity.',
    'land.art.you': 'you',
    'land.art.friend': 'friend',
    'land.photo': 'Photo',
    'land.videoPic': 'Video picture',
    'land.data': 'Geodata: Source: Federal Office of Topography <a href="https://www.swisstopo.admin.ch" target="_blank" rel="noopener">swisstopo</a> (© swisstopo)',
    'land.osm': 'Map data © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap contributors</a>',
    'land.odbl': 'Map data derived from OpenStreetMap is available on request under the <a href="https://opendatacommons.org/licenses/odbl/" target="_blank" rel="noopener">Open Database License (ODbL)</a>: <a href="mailto:hp@hpfreilabs.com">hp@hpfreilabs.com</a>.',
    'land.contact': 'Contact',
  },
  de: {
    'land.lang': 'Sprache',
    'land.overline': 'Echtes Gelände · Echter Multiplayer · <b>Läuft in der VR-Brille</b>',
    'land.tagline': 'Echte Berge, 1:1. Bildschirm wählen, rein da.',
    'land.live': 'Jetzt live',
    'land.loading': 'lädt…',
    'land.name': 'Dein Name',
    'land.rec': 'Empfohlen',
    'land.vr.pitch': 'Volle Immersion — steh auf dem Berg',
    'land.vr.need': 'Quest / WebXR-Headset',
    'land.flat.name': 'Desktop',
    'land.flat.pitch': 'Schnellster Start — klick und los',
    'land.flat.need': 'Tastatur + Maus',
    'land.mob.name': 'Handy',
    'land.mob.pitch': 'Mit GPS echt unterwegs',
    'land.mob.need': 'Touch + Neigen, quer',
    'land.off.vrXr': 'braucht ein WebXR-Headset (Quest-Browser)',
    'land.off.vrHttps': 'braucht HTTPS + ein WebXR-Headset',
    'land.off.mob': 'öffne die Seite auf dem Handy',
    'land.gps.use': '📍 GPS nutzen',
    'land.gps.asking': '📍 GPS: frage an…',
    'land.gps.fix': '📍 GPS ±{m} m',
    'land.gps.nofix': '📍 Kein GPS-Fix — tipp für Hilfe',
    'land.gps.off': '📍 GPS geht nicht — tipp für Hilfe',
    'land.gps.retry': 'Nochmal',
    'land.gps.dismiss': 'Schliessen',
    'land.voice': '🎤 Sprachchat an',
    'land.voice.ready': '🎤 Sprache bereit — Mikro offen ([M] / linker Trigger: stumm)',
    'land.voice.fail': '🎤 Mikro geht nicht: {err}',
    'land.loaderAria': 'Welt lädt',
    'land.status.connecting': 'verbinde…',
    'land.pop.some': '👥 {in} in der Welt · 👁 {map} auf der Karte{room}',
    'land.pop.none': '👥 noch niemand drin — sei zuerst da{room}',
    'land.pop.room': ' · Platz für {n}',
    'land.roster.you': 'du',
    'land.roster.in': 'in der Welt',
    'land.roster.map': 'auf der Karte',
    'land.roster.bot': '🤖 {n} Bot streift durchs Gelände',
    'land.roster.bots': '🤖 {n} Bots streifen durchs Gelände',
    'land.world.this': 'diese Welt',
    'land.world.real': 'echtes Gelände',
    'land.video.soon': 'Video folgt bald',
    'land.video.play': 'Video abspielen',
    'land.video.yt': 'Lädt erst beim Klick von YouTube (ohne Cookies)',
    'land.lede': 'Ein echter Ort, im <b>Massstab 1:1</b> nachgebaut aus vermessenen offenen Geodaten — Berge, Seen, Wälder, Dörfer und Bahnen genau dort, wo sie wirklich stehen. <strong>Hier ist kein Gelände erfunden.</strong>',
    'land.tiles': 'Was dich erwartet',
    'land.more': 'tipp für mehr',
    'land.f.together.h': 'Triff die anderen dort',
    'land.f.together.l': 'Gleiche Welt, live — hör sie, wo sie stehen',
    'land.f.together.d': 'Alle, die online sind, laufen durch dieselbe Welt, ihr Name über dem Avatar und auf der Karte. Schalt 🎤 ein und ihr hört euch dort, wo ihr steht — nah und klar neben dir, leise über den Hang.',
    'land.f.ride.h': 'Fahr, was die Einheimischen fahren',
    'land.f.ride.l': 'Standseilbahn, Gondel, Sessellift — echte Linien',
    'land.f.ride.d': 'Standseilbahnen, Gondeln, Luftseilbahnen und Sessellifte fahren auf ihren echten Trassen — an der Station einsteigen, hochfahren, an einer Station oder irgendwo unterwegs aussteigen.',
    'land.f.under.h': 'Ab in den Berg',
    'land.f.under.l': 'Gebohrte Tunnel, begehbare Viadukte',
    'land.f.under.d': 'Bahntunnel sind durch die Berge gebohrt und Viadukte überspannen die Schluchten, begehbar von Ende zu Ende — wie vermessen, nicht aufgemalt.',
    'land.f.far.h': 'Ein Horizont, der echt ist',
    'land.f.far.l': 'Benannte Gipfel bis 100 km weit',
    'land.f.far.d': 'Benannte Gipfel stehen bis 100 km weit am Horizont, abgesenkt durch die echte Erdkrümmung. Zoom rein und sie sind beschriftet, und der Entfernungsmesser liest die Distanz zu allem, was du anpeilst.',
    'land.f.any.h': 'Jeder Bildschirm',
    'land.f.any.l': 'VR, Desktop oder Handy — ⤳ Beam und los',
    'land.f.any.d': 'VR-Brille, Desktop oder Handy — Link öffnen, Ort auf der Karte wählen, ⤳ Beam. Auf dem Handy läufst du mit einem Stick und schaust mit dem Gyroskop umher; GPS setzt dich auf deine echte Position.',
    'land.ph.h': 'Dein Handy ist ein Fenster',
    'land.ph.s1': 'Quer halten',
    'land.ph.s2': 'Hochhalten und umdrehen — mit GPS zeigt es, was dahinter liegt',
    'land.ph.s3': 'Flach halten: die Karte',
    'land.rotate': '<b>Welten wechseln.</b> Es ist immer eine Welt live, und die kann jederzeit wechseln — der Berg von heute ist morgen vielleicht ein anderer.',
    'land.swiss': 'Das Projekt konzentriert sich auf die Schweiz und steht auf den exzellenten offenen Geodaten von <a href="https://www.swisstopo.admin.ch" target="_blank" rel="noopener">swisstopo</a> — LiDAR-Gelände auf einen halben Meter, grundrisstreue Gebäude, Luftbilder. Welten anderswo laufen auf globalen offenen Daten mit gröberer Auflösung.',
    'land.art.you': 'du',
    'land.art.friend': 'Freund',
    'land.photo': 'Foto',
    'land.videoPic': 'Videobild',
    'land.data': 'Geodaten: Quelle: Bundesamt für Landestopografie <a href="https://www.swisstopo.admin.ch" target="_blank" rel="noopener">swisstopo</a> (© swisstopo)',
    'land.osm': 'Kartendaten © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap-Mitwirkende</a>',
    'land.odbl': 'Aus OpenStreetMap abgeleitete Kartendaten sind auf Anfrage unter der <a href="https://opendatacommons.org/licenses/odbl/" target="_blank" rel="noopener">Open Database License (ODbL)</a> verfügbar: <a href="mailto:hp@hpfreilabs.com">hp@hpfreilabs.com</a>.',
    'land.contact': 'Kontakt',
  },
});
